import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Copy, Check } from 'lucide-react';
import styled from '@emotion/styled';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import useManualInput from './useManualInput';
import {
  Box, Field, Input, Select, TextArea, Row, Button, Note,
} from './manualInputStyles';
import {
  toRows, newRow, updateRow, removeRow, rowsToPayload, canSave, isDirty,
} from './futureScenariosForm';

const Sources = styled.div`display: grid; gap: 10px; margin-top: 10px;`;
const Source = styled.div`
  padding: 12px 14px; border-radius: 8px;
  border: 1px solid ${(p) => (p.$on ? 'rgba(201,167,255,0.7)' : 'rgba(255,255,255,0.1)')};
  background: ${(p) => (p.$on ? 'rgba(201,167,255,0.08)' : 'rgba(255,255,255,0.03)')};
  header { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
  strong { font-size: 14px; }
  p { margin: 6px 0 0; font-size: 13px; line-height: 1.5; color: rgba(255,255,255,0.65); }
  ol { margin: 6px 0 0; padding-left: 20px; font-size: 13px; color: rgba(255,255,255,0.8); }
`;
const Card = styled.div`
  padding: 12px 14px; margin-top: 10px; border-radius: 8px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
`;
const Count = styled.span`font-size: 12px; color: rgba(255,255,255,0.5);`;

/**
 * The forecast scenarios (#1062), set under Sisältö: where the worlds of the
 * Kuviteltu tulevaisuus courses come from. One source is in use: the experts' set
 * (the default), the workshop's own scenarios from a prerequisite module (read here,
 * never edited), or texts written for this workshop. The courses read the source
 * that is chosen and never fall back to another.
 */
export default function FutureScenariosAdmin({ instanceId, locked = false, onApplied }) {
  const { t } = useTranslation();
  const { context, busy, error, run } = useManualInput(instanceId, 'future_scenarios', { onApplied });
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState(null);
  const k = (key, params) => t(`futureReimagined.scenarioEditor.${key}`, params);

  useEffect(() => {
    if (context) setRows(toRows(context.own));
  }, [context]);

  const limits = context?.limits || { min: 2, max: 6, title: 120, text: 20000 };
  const disabled = busy || locked || !context;
  const change = (key, patch) => { setRows((r) => updateRow(r, key, patch)); setNote(null); };
  const choose = async (source) => {
    const data = await run('apply', { source });
    if (data) setNote(k('saved'));
  };
  const save = async () => {
    const data = await run('apply', { scenarios: rowsToPayload(rows) });
    if (data) setNote(k('saved'));
  };
  const resetOwn = async () => {
    const data = await run('apply', { resetOwn: true });
    if (data) { setRows([]); setNote(k('saved')); }
  };

  const listed = (list) => <ol>{list.map((s) => <li key={s.id}>{s.level != null ? `${s.level} · ` : ''}{s.title}</li>)}</ol>;
  const sourceBox = (id, body) => {
    const on = context?.source === id;
    return (
      <Source key={id} $on={on}>
        <header>
          <strong>{t(`futureReimagined.scenario.sources.${id}`)}</strong>
          {on
            ? <Count><Check size={13} /> {k('inUse')}</Count>
            : <Button type="button" onClick={() => choose(id)} disabled={disabled}>{k('use')}</Button>}
        </header>
        <p>{k(`sourceHint.${id}`)}</p>
        {body}
      </Source>
    );
  };

  return (
    <Box>
      <h3>{k('title')}</h3>
      <p className="intro">{k('intro')}</p>
      {!context && error && <Note $error>{k('loadError', { message: error })}</Note>}
      {context && (
        <Sources role="group" aria-label={k('source')}>
          {sourceBox('expert', <>{listed(context.expert)}{context.placeholder && <Note $warn>{k('placeholder')}</Note>}</>)}
          {sourceBox('workshop', context.workshop.length ? listed(context.workshop) : <Note $warn>{k('workshopEmpty')}</Note>)}
          {sourceBox('own', (
            <>
              {rows.map((r, i) => (
                <Card key={r.key}>
                  <Row style={{ marginTop: 0 }}>
                    <Field style={{ flex: '2 1 260px' }}>
                      {k('titleLabel')}
                      <Input value={r.title} maxLength={limits.title} onChange={(e) => change(r.key, { title: e.target.value })} disabled={disabled} />
                    </Field>
                    <Field style={{ flex: '0 1 160px' }}>
                      {k('levelLabel')}
                      <Select value={r.level} onChange={(e) => change(r.key, { level: e.target.value })} disabled={disabled}>
                        <option value="">{k('levelNone')}</option>
                        {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={String(n)}>{n}</option>)}
                      </Select>
                    </Field>
                  </Row>
                  <Field style={{ marginTop: 8 }}>
                    {k('textLabel')} <Count>{k('chars', { n: r.text.length, max: limits.text })}</Count>
                    <TextArea style={{ minHeight: 160 }} value={r.text} maxLength={limits.text} onChange={(e) => change(r.key, { text: e.target.value })} disabled={disabled} aria-label={`${k('textLabel')} ${i + 1}`} />
                  </Field>
                  <Row>
                    <Button type="button" onClick={() => setRows((x) => removeRow(x, r.key))} disabled={disabled}><Trash2 size={15} /> {k('remove')}</Button>
                  </Row>
                </Card>
              ))}
              <Row>
                <Button type="button" onClick={() => setRows((x) => [...x, newRow()])} disabled={disabled || rows.length >= limits.max}><Plus size={16} /> {k('add')}</Button>
                {rows.length === 0 && (
                  <Button type="button" onClick={() => setRows(toRows(context.expert.map(({ title, text, level }) => ({ title, text, level }))))} disabled={disabled}>
                    <Copy size={15} /> {k('copyExpert')}
                  </Button>
                )}
                <Button type="button" $primary onClick={save} disabled={disabled || !canSave(rows, limits.min) || !isDirty(rows, context.own)}>
                  <Save size={16} /> {busy ? t('manualInputs.saving') : k('save')}
                </Button>
                {context.own.length > 0 && <Button type="button" onClick={resetOwn} disabled={disabled}>{k('reset')}</Button>}
              </Row>
              {rows.length > 0 && !canSave(rows, limits.min) && <Note $warn>{k('tooFew', { min: limits.min })}</Note>}
            </>
          ))}
        </Sources>
      )}
      {note && <Note>{note}</Note>}
      {context && error && <Note $error>{error}</Note>}
      {locked && <Note $warn>{t('manualInputs.locked')}</Note>}
    </Box>
  );
}
